import { FadeIn } from "./FadeIn";
import {
  Phone, FileText, Network, Copy, ClipboardList, PhoneIncoming,
  FileSpreadsheet, Boxes, Users, Brain, Repeat, EyeOff,
} from "lucide-react";

const PROBLEMS = [
  { icon: Phone, title: "Constant customer phone calls", desc: "Staff interrupted throughout the day for information that should be visible." },
  { icon: FileText, title: "Manual quoting", desc: "Every quote built from scratch, regardless of how routine the job is." },
  { icon: Network, title: "Information spread across different systems", desc: "Customer, job and stock details living in disconnected places." },
  { icon: Copy, title: "Repeated data entry", desc: "The same information typed into multiple systems, multiple times." },
  { icon: ClipboardList, title: "Management chasing job updates", desc: "Progress known only by walking the floor or making calls." },
  { icon: PhoneIncoming, title: "Customers calling for job status", desc: "Routine status enquiries consuming administrative time." },
  { icon: FileSpreadsheet, title: "Paper or spreadsheet-based workflows", desc: "Critical processes dependent on files only one person maintains." },
  { icon: Boxes, title: "Disconnected stock and purchasing", desc: "Materials ordered reactively, with no live view of levels." },
  { icon: Users, title: "Staff relying on one person for information", desc: "Operations slowing whenever a key person is unavailable." },
  { icon: Brain, title: "Important information living in someone\u2019s head", desc: "Undocumented knowledge the business cannot afford to lose." },
  { icon: Repeat, title: "Duplicated administration", desc: "Office effort repeated across quoting, scheduling and invoicing." },
  { icon: EyeOff, title: "Limited visibility across operations", desc: "No single place to see what is actually happening in the business." },
];

export const Problem = () => (
  <section className="bg-white border-b border-gray-200" data-testid="problem-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
      <FadeIn className="max-w-3xl">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">The Problem</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="problem-heading">
          Your business may have grown. Have your systems grown with it?
        </h2>
        <p className="mt-5 text-base md:text-lg text-zinc-600 leading-relaxed">
          Many successful businesses reach a point where the company has grown substantially, but the systems
          behind it are still based on processes developed when the company was much smaller.
        </p>
      </FadeIn>
      <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-gray-200" data-testid="problem-cards-grid">
        {PROBLEMS.map((p, i) => (
          <FadeIn key={p.title} delay={(i % 3) * 0.07} className="border-b border-r border-gray-200">
            <div className="p-6 lg:p-7 h-full hover:bg-offwhite transition-colors duration-200">
              <p.icon size={20} strokeWidth={1.75} className="text-forest" />
              <h3 className="mt-4 font-heading font-semibold text-charcoal text-[15px] leading-snug">{p.title}</h3>
              <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{p.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
      <FadeIn className="mt-12 max-w-3xl">
        <p className="text-base md:text-lg text-charcoal font-medium leading-relaxed border-l-2 border-sage pl-4" data-testid="problem-closing">
          These systems may have worked perfectly when the business was smaller. The problem begins when growth
          means management simply has to work harder to keep everything connected.
        </p>
      </FadeIn>
    </div>
  </section>
);

export const WhatWeDo = () => (
  <section id="what-we-do" className="bg-offwhite border-b border-gray-200 scroll-mt-20" data-testid="what-we-do-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-12">
      <FadeIn className="lg:col-span-5">
        <p className="text-[11px] font-semibold tracking-[0.28em] text-forest uppercase mb-5">What We Do</p>
        <h2 className="font-heading font-bold tracking-tight text-charcoal text-2xl sm:text-3xl lg:text-4xl leading-tight" data-testid="what-we-do-heading">
          We build the systems behind the business.
        </h2>
      </FadeIn>
      <FadeIn delay={0.1} className="lg:col-span-7 space-y-5 text-base md:text-lg text-zinc-600 leading-relaxed">
        <p>Westline Technologies looks at how your business actually operates from beginning to end.</p>
        <p>
          We identify where information is being collected repeatedly, where staff are waiting on management,
          where customers are making unnecessary calls, where administration is duplicated and where disconnected
          software is creating additional work.
        </p>
        <p>We then design a digital operating system around those workflows.</p>
        <div className="pt-2 grid sm:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
          <div className="bg-offwhite p-6">
            <p className="text-sm text-zinc-500">The objective is not</p>
            <p className="mt-1 font-heading font-semibold text-charcoal">automation for the sake of automation.</p>
          </div>
          <div className="bg-forest p-6">
            <p className="text-sm text-sage-light">The objective is</p>
            <p className="mt-1 font-heading font-semibold text-white leading-snug">
              to automate and centralise where it produces genuine commercial value — while keeping human
              judgement where it matters.
            </p>
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);
